import {
  DiscoveryApi,
  FetchApi,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { FieldExtensionComponentProps, createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';

type GcpProjectValue = {
  gcpId?: string;
  gcpNumber?: string;
};

type GcpProject = { projectId: string; projectNumber: string };

export async function fetchAllowedGcpProjects(
  fetchApi: FetchApi,
  discoveryApi: DiscoveryApi,
): Promise<GcpProject[]> {
  const baseUrl = await discoveryApi.getBaseUrl('gcp');
  const res = await fetchApi.fetch(`${baseUrl}/projects`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Failed to fetch allowed projects (${res.status}): ${text}`);
  }
  const body = await res.json();
  const projects = body?.projects;
  if (!Array.isArray(projects)) return [];
  return projects.filter(
    (project: unknown): project is GcpProject =>
      typeof project === 'object' &&
      project !== null &&
      typeof (project as GcpProject).projectId === 'string' &&
      typeof (project as GcpProject).projectNumber === 'string',
  );
}

export const GCPProjectField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<GcpProjectValue>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const value = formData ?? {};
  const selectedProjectId = value.gcpId ?? '';
  const [allowedProjects, setAllowedProjects] = useState<GcpProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState<string | undefined>(undefined);
  // Ref pattern: keeps the latest onChange without adding it as an effect dependency.
  // onChange comes from the scaffolder framework and isn't memoized, so including it
  // in the dep array would cause infinite re-renders. The effect should only re-run
  // when data changes (selectedProjectId, allowedProjects, gcpNumber), not when the callback reference changes.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Fetch allowed GCP projects once when APIs are available.
  useEffect(() => {
    let cancelled = false;
    setErr(undefined);
    fetchAllowedGcpProjects(fetchApi, discoveryApi)
      .then(projects => {
        if (!cancelled) setAllowedProjects(projects);
      })
      .catch(e => {
        if (!cancelled) setErr(e?.message ?? 'Failed to load projects');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi]);

  // Sync gcpNumber when the list has loaded. Do not clear selection while allowedProjects is still empty — that happens
  // briefly when navigating back to the form and would wipe restored gcpProject before projects load.
  useEffect(() => {
    if (!selectedProjectId) return;
    const project = allowedProjects.find(p => p.projectId === selectedProjectId);
    if (!project) {
      if (!isLoading && allowedProjects.length > 0 && value.gcpNumber) {
        onChangeRef.current({ gcpId: undefined, gcpNumber: undefined });
      }
      return;
    }
    if (project.projectNumber && value.gcpNumber !== project.projectNumber) {
      onChangeRef.current({ gcpId: selectedProjectId, gcpNumber: project.projectNumber });
    }
  }, [selectedProjectId, allowedProjects, value.gcpNumber, isLoading]);

  const handleChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const projectId = (e.target.value as string) ?? '';
    if (!projectId) {
      onChange({ gcpId: undefined, gcpNumber: undefined });
      return;
    }
    const project = allowedProjects.find(p => p.projectId === projectId);
    onChange({
      gcpId: projectId,
      gcpNumber: project?.projectNumber ?? undefined,
    });
  };

  return (
    <FormControl margin="normal" required={required} error={(rawErrors?.length ?? 0) > 0 && !selectedProjectId}>
      <InputLabel id="gcpId-label">GCP project name</InputLabel>
      <Select
        labelId="gcpId-label"
        id="gcpId"
        value={selectedProjectId}
        onChange={handleChange}
      >
        {allowedProjects.map(project => (
          <MenuItem key={project.projectId} value={project.projectId}>
            {project.projectId}
          </MenuItem>
        ))}
      </Select>
      {isLoading ? <CircularProgress size={18} /> : null}
      <FormHelperText id="entityName">
        Select the GCP project the app belongs to
        {err ? ` — ${err}` : ''}
      </FormHelperText>
      <input type="hidden" value={value.gcpNumber ?? ''} readOnly aria-hidden />
    </FormControl>
  );
};

export const GCPProjectValidate = (
  value: GcpProjectValue | undefined,
  validation: FieldValidation,
) => {
  if (!value?.gcpId) {
    validation.addError('Select a GCP project.');
    return;
  }
  if (!value?.gcpNumber) {
    validation.addError('Could not resolve GCP project number for the selected project.');
  }
};

export const GcpProjectFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GCPProjectField',
    component: GCPProjectField,
    validation: GCPProjectValidate,
  }),
);
